/**
 * Initialize reCAPTCHA if grecaptcha is available.
 */
export const initializeCaptcha = () => {
    if (typeof grecaptcha !== 'undefined' && grecaptcha.ready) {
        grecaptcha.ready(() => {
            grecaptcha
                .execute(var_from_php.recaptcha_site_key, { action: 'submit' })
                .then((token) => {
                    document.getElementById('recaptcha-response').value = token;
                });
        });
    } else {
        console.error('reCAPTCHA library is not available.');
    }
};

/**
 * Helper function to hide all error messages.
 */
const hideAllErrors = () => {
    const errorMessages = document.querySelectorAll('.js-form-error');
    errorMessages &&
        errorMessages.forEach((errorMessage) => {
            errorMessage.classList.remove('show');
        });
    const validateErrors = document.querySelectorAll('.js-validate.error');
    validateErrors &&
        validateErrors.forEach((el) => {
            el.classList.remove('error');
        });
};

/**
 * Show error for specified HTML element.
 *
 * @param {HTMLElement} element - The HTML element to which the class should be added.
 * @param {string} className - The class name to add to the element.
 */
const showError = (element, className) => {
    const parent = element.closest('.js-form-control');
    const error = parent.querySelector(className);
    error && error.classList.add('show');
};

/**
 * Validates an email address using a regular expression.
 *
 * @param {string} email - The email address to be validated.
 * @return {boolean} True if the email is valid, false otherwise.
 */
const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
};

/**
 * Check if a given value is valid from a security perspective.
 *
 * @param {string} value - The value to be checked for security vulnerabilities.
 * @return {boolean} True if the value is considered secure, false if security issues are detected.
 */
const isValidSecurityValue = (value) => {
    if (!value) return;

    // Check for Local File Inclusion (LFI)
    if (
        value.includes('../') ||
        value.startsWith('/etc/passwd') ||
        value.includes('..\\') ||
        value.startsWith('C:\\Windows')
    ) {
        return false;
    }

    // Check for SQL Injection
    const sqlKeywords = [
        'SELECT',
        'INSERT',
        'UPDATE',
        'DELETE',
        'UNION',
        'WHERE',
        'AND',
        'OR',
    ];
    const sanitizedValue = value.toUpperCase(); // Convert to uppercase for case-insensitive check
    if (
        sqlKeywords.some((keyword) => sanitizedValue.includes(` ${keyword} `))
    ) {
        // Add spaces around the keyword to ensure it's a separate word
        return false;
    }

    // Check for Cross-Site Scripting (XSS)
    const sanitizedInput = value.replace(/<|>/g, ''); // Remove < and > characters
    if (value !== sanitizedInput) {
        return false;
    }

    // Additional custom security checks may be added as needed.

    // If the value passes all security checks, consider it valid
    return true;
};

/**
 * Displays an error message on a web page by setting the inner HTML of an element
 * with the class 'js-submit-error' and adding the 'show' class to make it visible.
 *
 * @param {string} errorText - The error message to display.
 */
export const showSubmitError = (errorText) => {
    // Get submit error tag
    const submitError = document.querySelector('.js-submit-error');
    if (submitError) {
        submitError.innerHTML = errorText; // Use errorText instead of error
        submitError.classList.add('show');
    }
};

/**
 * Verify reCAPTCHA response with the server.
 *
 * @param {string} recaptchaResponse - The reCAPTCHA response token to be verified.
 * @return {Promise<boolean | Object>} A Promise that resolves to a boolean (false in case of an error) or an object with the verification result.
 */
export const recaptchaSiteVerify = async (recaptchaResponse) => {
    const formData = new FormData();
    formData.append('recaptcha_response', recaptchaResponse);
    formData.append('action', 'recaptcha_site_verify');
    formData.append('nonce', var_from_php.nonce);

    try {
        const response = await fetch(var_from_php.ajax_url, {
            method: 'POST',
            body: formData,
        });
        if (response.ok) {
            const resp = await response.json(); // Assuming response is an object
            console.log(resp.data);
            return resp.data;
        }
        throw new Error(`${response.status} ${response.statusText}`);
    } catch (error) {
        // Return false in case of an error
        return false;
    }
};

/**
 * Validates form fields and displays error messages for invalid fields.
 *
 * @param {NodeList|HTMLCollection} formControl - A collection of form elements to validate.
 * @return {boolean} - `true` if all fields are valid, `false` if any field is invalid.
 */
const validateFields = (formControl) => {
    let validationStatus = true;
    formControl &&
        formControl.forEach((el) => {
            const validate = el.querySelector('.js-validate');
            if (validate) {
                const tagType = validate?.tagName?.toLowerCase();
                if (tagType === 'input') {
                    const inputType = validate.getAttribute('type');
                    if (
                        inputType === 'email' ||
                        inputType === 'text' ||
                        inputType === 'number' ||
                        inputType === 'file'
                    ) {
                        const inputValue = validate.value.trim();
                        if (inputType === 'email') {
                            if (!inputValue) {
                                validationStatus = false;
                                showError(el, '.js-empty-field');
                                validate.classList.add('error');
                            } else if (!validateEmail(inputValue)) {
                                validationStatus = false;
                                showError(el, '.js-wrong-email');
                                validate.classList.add('error');
                            }
                        } else if (inputType === 'text') {
                            if (!inputValue) {
                                validationStatus = false;
                                showError(el, '.js-empty-field');
                                validate.classList.add('error');
                            }
                        } else if (inputType === 'number') {
                            if (!inputValue) {
                                validationStatus = false;
                                showError(el, '.js-empty-field');
                                validate.classList.add('error');
                            } else if (Number.isNaN(inputValue)) {
                                validationStatus = false;
                                showError(el, '.js-wrong-number');
                                validate.classList.add('error');
                            }
                        } else if (
                            inputType === 'file' &&
                            validate.files.length > 0
                        ) {
                            const { files } = validate;
                            // Array of valid file extensions
                            const validExtensions = [
                                'png',
                                'jpg',
                                'jpeg',
                                'pdf',
                            ];
                            // Maximum allowed file size in bytes (5MB)
                            const maxSize = 5 * 1024 * 1024; // 5MB in bytes
                            // Name of the selected file
                            const fileName = files[0].name;
                            // Size of the selected file in bytes
                            const fileSize = files[0].size;
                            // Extension of the selected file (lowercase)
                            const fileExtension = fileName
                                .split('.')
                                .pop()
                                .toLowerCase();

                            // Check file type
                            if (validExtensions.indexOf(fileExtension) === -1) {
                                validationStatus = false;
                                showError(el, '.js-invalid-type');
                            }
                            // Check file size
                            if (fileSize > maxSize) {
                                validationStatus = false;
                                showError(el, '.js-invalid-size');
                            }
                        }
                    } else if (inputType === 'radio') {
                        const checkedRadio = el.querySelector(
                            'input[type="radio"]:checked'
                        ).value;
                        if (!checkedRadio) {
                            validationStatus = false;
                            showError(el, '.js-empty-field');
                            validate.classList.add('error');
                        }
                    }
                } else if (tagType === 'select') {
                    const selectValue = validate.value.trim();
                    if (selectValue === '') {
                        validationStatus = false;
                        showError(el, '.js-empty-field');
                        validate.classList.add('error');
                    }
                } else if (tagType === 'textarea') {
                    const inputValue = validate.value.trim();
                    if (!inputValue) {
                        validationStatus = false;
                        showError(el, '.js-empty-field');
                        validate.classList.add('error');
                    }
                }
            }
        });

    return validationStatus;
};

/**
 * Initialize form validation for a given HTML form element.
 *
 * @param {HTMLFormElement} form - The form element to be validated.
 */
export const initFormValidation = (form) => {
    // Hide all errors before re-validating.
    hideAllErrors();

    const formControl = form.querySelectorAll('.js-form-control');

    return validateFields(formControl);
};
